import { getRegistryConfig, type RegistryConfig as SDKRegistryConfig } from "@breakout/sdk"
import { useQuery } from "@tanstack/react-query"
import { useAdminConfig } from "../lib/providers"

/** Hook return type - extends SDK's RegistryConfig with loading/error states */
export interface RegistryConfigState extends Partial<SDKRegistryConfig> {
	isLoading: boolean
	error: Error | null
}

/**
 * Hook to read operator, resolver, and treasury addresses from the registry contract on-chain.
 * These values can change on-chain and should always be read from the registry, not from static config.
 */
export function useRegistryConfig(): RegistryConfigState {
	const { chainConfig, publicClient } = useAdminConfig()

	const { data, isLoading, error } = useQuery({
		queryKey: ["registryConfig", chainConfig.registry],
		queryFn: async (): Promise<SDKRegistryConfig> => {
			return getRegistryConfig(publicClient, {
				registry: chainConfig.registry,
			})
		},
		staleTime: 60_000, // 1 minute - registry config rarely changes
	})

	return {
		operator: data?.operator,
		resolver: data?.resolver,
		treasury: data?.treasury,
		isLoading,
		error: error ?? null,
	}
}
